import {
  Alert,
  Anchor,
  Box,
  Combobox,
  FormField,
  FormHelp,
  Input,
  Label,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';
import { Controller, type FieldValues, useFormContext } from 'react-hook-form';
import { get } from 'lodash-es';
import { AddressAutocomplete, type AddressResponse } from '../../../AddressAutocomplete';
import { useAddressAutocomplete } from '../../../../hooks/useAddressAutocomplete';
import { useValidateAddress } from '../../../../hooks/useValidateAddress';
import { FIELD_IDS } from '../../../../constants/wizard';
import { MaskedZipCodeInput, validateZipCode } from '../../../Inputs';
import { useRef, useState } from 'react';
import { useMount } from 'ahooks';
import { US_STATES_ABBR_LIST_EXCLUDING_TERRITORIES } from '../../../../constants/applicationsConstants';
import { getFormattedAddress } from '@zg-rentals/applications-utils';

type CurrentAddressProps = {
  onZipTerritoryChange: (isTerritory: boolean) => void;
};

export function CurrentAddress({ onZipTerritoryChange }: CurrentAddressProps) {
  const {
    formState: { errors },
    getValues,
    setValue,
    register,
    trigger,
    watch,
  } = useFormContext();

  const validateAddress = useValidateAddress();

  // Identifies the most recent address check so out-of-order async responses don't overwrite a newer result.
  const latestZipCheckId = useRef(0);
  const skipAddressApiCheck = useRef(false);

  const validateZip = async (value: string, formValues: FieldValues) => {
    const formatResult = validateZipCode(value);
    if (formatResult !== true) {
      latestZipCheckId.current += 1;
      onZipTerritoryChange(false);
      return formatResult;
    }
    const city = get(formValues, FIELD_IDS.CURRENT_RESIDENCE_CITY);
    const state = get(formValues, FIELD_IDS.CURRENT_RESIDENCE_STATE);
    if (!city || !state || skipAddressApiCheck.current) {
      latestZipCheckId.current += 1;
      onZipTerritoryChange(false);
      return true;
    }
    const checkId = (latestZipCheckId.current += 1);
    const { isValid, errorMessage } = await validateAddress({ city, state, zipcode: value });
    if (checkId === latestZipCheckId.current) {
      onZipTerritoryChange(!isValid);
    }
    return isValid || errorMessage || 'Enter a ZIP code in one of the 50 states or Washington, D.C. to continue';
  };

  const validateAddressState = (state?: string | null) => {
    if (!state || US_STATES_ABBR_LIST_EXCLUDING_TERRITORIES.includes(state.toUpperCase())) {
      return true;
    }
    return 'Enter an address in one of the 50 states or Washington, D.C. to continue';
  };

  const zpid = watch(FIELD_IDS.CURRENT_RESIDENCE_ZPID);
  const streetValue = watch(FIELD_IDS.CURRENT_RESIDENCE_STREET);
  const cityValue = watch(FIELD_IDS.CURRENT_RESIDENCE_CITY);
  const stateValue = watch(FIELD_IDS.CURRENT_RESIDENCE_STATE);
  const zipValue = watch(FIELD_IDS.CURRENT_RESIDENCE_ZIP);

  const addressValue = getFormattedAddress(
    streetValue
      ? {
          street: streetValue,
          city: cityValue,
          state: stateValue,
          zip: zipValue,
        }
      : undefined,
  );

  // If the user has an address entered previously that was not validated, show the manual address form
  const [isManualAddress, setIsManualAddress] = useState(!zpid && streetValue);
  const [isEditingAddress, setIsEditingAddress] = useState(!streetValue);

  // Validate a saved ZIP on mount without calling the API; the API check runs on the next blur, selection, or Next.
  useMount(() => {
    if (isManualAddress && zipValue) {
      skipAddressApiCheck.current = true;
      trigger(FIELD_IDS.CURRENT_RESIDENCE_ZIP).finally(() => {
        skipAddressApiCheck.current = false;
      });
    }
  });

  // validateAddress runs as part of ZIP validation, but it depends on city and state too.
  // Re-trigger it when the user leaves City or picks a State, only once all three fields are filled in.
  const revalidateZip = () => {
    const addressFields = getValues([
      FIELD_IDS.CURRENT_RESIDENCE_CITY,
      FIELD_IDS.CURRENT_RESIDENCE_STATE,
      FIELD_IDS.CURRENT_RESIDENCE_ZIP,
    ]);
    if (addressFields.every(Boolean)) {
      trigger(FIELD_IDS.CURRENT_RESIDENCE_ZIP);
    }
  };

  const [partialAddress, setPartialAddress, queryData] = useAddressAutocomplete({});

  const errorStreet = get(errors, FIELD_IDS.CURRENT_RESIDENCE_STREET);
  const errorCity = get(errors, FIELD_IDS.CURRENT_RESIDENCE_CITY);
  const errorState = get(errors, FIELD_IDS.CURRENT_RESIDENCE_STATE);
  const errorZip = get(errors, FIELD_IDS.CURRENT_RESIDENCE_ZIP);

  function onEditAddress() {
    setIsEditingAddress(true);
    onZipTerritoryChange(false);

    setValue(FIELD_IDS.CURRENT_RESIDENCE_ZPID, null);
    setValue(FIELD_IDS.CURRENT_RESIDENCE_STREET, null);
    setValue(FIELD_IDS.CURRENT_RESIDENCE_CITY, null);
    setValue(FIELD_IDS.CURRENT_RESIDENCE_STATE, null);
    setValue(FIELD_IDS.CURRENT_RESIDENCE_ZIP, null);
  }

  function onClickManualAddress() {
    setValue(FIELD_IDS.CURRENT_RESIDENCE_STREET, partialAddress);
    trigger(FIELD_IDS.CURRENT_RESIDENCE_STREET);
    setIsManualAddress(true);
  }

  return (
    <>
      <Controller
        name={FIELD_IDS.CURRENT_RESIDENCE_STREET}
        rules={{
          required: 'A USPS-validated address is required',
          validate: (_value, formValues) => validateAddressState(get(formValues, FIELD_IDS.CURRENT_RESIDENCE_STATE)),
        }}
        render={({ field: { onChange, value, ...rest } }) => {
          const handleAddressSelect = (address: AddressResponse) => {
            setIsEditingAddress(false);
            onZipTerritoryChange(false);
            setValue(FIELD_IDS.CURRENT_RESIDENCE_ZPID, Number(address.zpid));
            onChange(address.street);
            setValue(FIELD_IDS.CURRENT_RESIDENCE_CITY, address.city);
            setValue(FIELD_IDS.CURRENT_RESIDENCE_STATE, address.state);
            setValue(FIELD_IDS.CURRENT_RESIDENCE_ZIP, address.zip);
            trigger(FIELD_IDS.CURRENT_RESIDENCE_STREET);
          };

          return (
            <FormField
              label={<Label id="current-residence-address-label">{isManualAddress ? 'Street' : 'Address'}</Label>}
              disabled={!isManualAddress && !isEditingAddress}
              control={
                !isManualAddress ? (
                  <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <Box css={{ display: 'block', flexGrow: '1' }}>
                      <AddressAutocomplete
                        {...rest}
                        value={addressValue}
                        partialAddress={partialAddress}
                        onSetPartialAddress={setPartialAddress}
                        queryData={queryData}
                        onAddressSelect={handleAddressSelect}
                        renderEmptyState={
                          <Paragraph>
                            Address not found. If you are at an address that isn’t found,{' '}
                            <Anchor asChild>
                              <button type="button" onClick={onClickManualAddress}>
                                enter it manually
                              </button>
                            </Anchor>
                          </Paragraph>
                        }
                        error={!!errorStreet}
                        disabled={!isEditingAddress}
                        aria-labelledby="current-residence-address-label"
                      />
                    </Box>
                    {!isEditingAddress && (
                      <Box css={{ marginLeft: 'sm' }}>
                        <TextButton onClick={onEditAddress}>Edit</TextButton>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Input value={value} onChange={onChange} aria-labelledby="current-residence-address-label" />
                )
              }
              description={errorStreet && <FormHelp>{String(errorStreet.message)}</FormHelp>}
              error={!!errorStreet}
            />
          );
        }}
      />
      {isManualAddress && (
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
          <FormField
            label={<Label>City</Label>}
            control={
              <Input
                maxLength={32}
                {...register(FIELD_IDS.CURRENT_RESIDENCE_CITY, {
                  required: 'City is required',
                  onBlur: revalidateZip,
                })}
              />
            }
            description={errorCity && <FormHelp>{String(errorCity.message)}</FormHelp>}
            error={!!errorCity}
          />
          <Controller
            name="currentResidence.address.state"
            rules={{ required: 'State is required' }}
            render={({ field }) => (
              <FormField
                label={<Label id="state-label">State</Label>}
                control={
                  <Combobox
                    {...field}
                    onChange={(value) => {
                      field.onChange(value);
                      revalidateZip();
                    }}
                    value={field.value || ''}
                    placeholder="Select a state"
                    options={US_STATES_ABBR_LIST_EXCLUDING_TERRITORIES}
                    size="md"
                    aria-labelledby="state-label"
                  />
                }
                description={errorState && <FormHelp>{String(errorState.message)}</FormHelp>}
                error={!!errorState}
              />
            )}
          />
          <FormField
            label={<Label id="zip-label">ZIP code</Label>}
            control={
              <Controller
                name="currentResidence.address.zip"
                rules={{
                  required: 'ZIP code is required',
                  validate: validateZip,
                }}
                render={({ field }) => <MaskedZipCodeInput {...field} aria-labelledby="zip-label" />}
              />
            }
            description={errorZip && <FormHelp>{String(errorZip.message)}</FormHelp>}
            error={!!errorZip}
          />
        </Box>
      )}
      <Alert tone="info" css={{ marginTop: 'tight' }}>
        Sales tax may be added at checkout depending on your address
      </Alert>
    </>
  );
}
